/**
 * The audio facade. Everything outside `src/audio/` talks to this and nothing
 * else.
 *
 * Three things it guarantees:
 *
 *  - **It never throws.** No `AudioContext`, a locked context, a browser that
 *    refuses to build a `ConvolverNode` — all of it degrades to a silent no-op
 *    with the same shape. A game does not crash because a laptop has no
 *    speakers.
 *  - **It respects autoplay policy.** The context is built inside `init()`,
 *    which must run from a user gesture. If the browser still refuses, the
 *    facade arms one-shot pointer/key listeners and unlocks on the next real
 *    interaction. `unlocked` tells you where you are.
 *  - **Volumes survive a reload.** Master/music/SFX live in localStorage.
 */

import { VIEW } from '@/core/config';
import { SynthEngine } from './Synth';
import { Music } from './Music';
import type { MusicState } from './Music';
import { playSfx, shootSfx, deathSfx } from './sfx';
import type { SfxHandle, SfxName, SfxOptions } from './sfx';

export type { MusicState } from './Music';
export type { SfxHandle, SfxName, SfxOptions } from './sfx';
export { SFX, TOWER_FAMILIES, shootSfx, deathSfx } from './sfx';

export type BusName = 'master' | 'music' | 'sfx';

export interface AudioSettings {
  master: number;
  music: number;
  sfx: number;
  muted: boolean;
}

const STORAGE_KEY = 'pixeltd.audio.v1';
const DEFAULTS: AudioSettings = { master: 0.8, music: 0.55, sfx: 0.9, muted: false };

/** Buffer x (0..VIEW.width) → pan (-1..1), with the edges pulled in slightly. */
export function panForX(x: number): number {
  const p = (x / VIEW.width) * 2 - 1;
  return (p < -1 ? -1 : p > 1 ? 1 : p) * 0.85;
}

export interface AudioFacade {
  /** True once a context exists and is running. */
  readonly unlocked: boolean;
  /** Call from a click/keypress. Safe to call repeatedly. */
  init(): Promise<boolean>;
  play(name: SfxName, opts?: SfxOptions): SfxHandle | null;
  setBus(name: BusName, gain: number): void;
  getBus(name: BusName): number;
  setMuted(v: boolean): void;
  duck(ms: number, amount?: number): void;
  setMusicState(state: MusicState): void;
  setMusicIntensity(v: number): void;
  setEnemyPressure(v: number): void;
  suspend(): void;
  resume(): void;
  panic(): void;
  dispose(): void;
}

function loadSettings(): AudioSettings {
  const out: AudioSettings = { ...DEFAULTS };
  try {
    if (typeof localStorage === 'undefined') return out;
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) return out;
    const parsed = JSON.parse(raw) as Partial<AudioSettings>;
    if (typeof parsed.master === 'number') out.master = parsed.master;
    if (typeof parsed.music === 'number') out.music = parsed.music;
    if (typeof parsed.sfx === 'number') out.sfx = parsed.sfx;
    if (typeof parsed.muted === 'boolean') out.muted = parsed.muted;
  } catch {
    // A corrupt or blocked store is not worth a single line of user-facing text.
  }
  return out;
}

function saveSettings(s: AudioSettings): void {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  } catch {
    // Private browsing. Volume simply will not persist.
  }
}

type AudioCtor = new (options?: AudioContextOptions) => AudioContext;

function audioCtor(): AudioCtor | null {
  const g = globalThis as unknown as { AudioContext?: AudioCtor; webkitAudioContext?: AudioCtor };
  return g.AudioContext ?? g.webkitAudioContext ?? null;
}

const clamp = (v: number, lo: number, hi: number) => (v < lo ? lo : v > hi ? hi : v);

class GameAudio implements AudioFacade {
  private engine: SynthEngine | null = null;
  private score: Music | null = null;
  private settings: AudioSettings = loadSettings();
  private ready = false;
  private initPromise: Promise<boolean> | null = null;
  private gestureArmed = false;
  private wantedState: MusicState = 'silent';
  private wantedIntensity = 0;
  private wantedPressure = 0;
  private seed = 'pixeltd-score';

  get unlocked(): boolean {
    return this.ready && this.engine !== null && this.engine.ctx.state === 'running';
  }

  /** Seed for the generative score. Set before `init()` to change the track. */
  setSeed(seed: string): void { this.seed = seed; }

  init(): Promise<boolean> {
    if (this.initPromise !== null) return this.initPromise;
    this.initPromise = this.build();
    return this.initPromise;
  }

  private async build(): Promise<boolean> {
    const Ctor = audioCtor();
    if (Ctor === null) return false;

    let ctx: AudioContext;
    try {
      ctx = new Ctor({ latencyHint: 'interactive' });
    } catch {
      return false;
    }

    try {
      this.engine = new SynthEngine(ctx);
      this.score = new Music(this.engine, this.seed);
    } catch {
      // A partial graph is worse than none. Drop it and stay silent.
      this.engine = null;
      this.score = null;
      try { await ctx.close(); } catch { /* already gone */ }
      return false;
    }

    this.applyVolumes();
    this.bindLifecycle();

    try { await ctx.resume(); } catch { /* handled below */ }

    if (ctx.state !== 'running') {
      this.armGesture();
      return false;
    }

    this.ready = true;
    this.score.start();
    this.score.setIntensity(this.wantedIntensity);
    this.score.setPressure(this.wantedPressure);
    this.score.setState(this.wantedState);
    return true;
  }

  /** Autoplay policy fallback: unlock on the next real interaction. */
  private armGesture(): void {
    if (this.gestureArmed || typeof window === 'undefined') return;
    this.gestureArmed = true;
    const unlock = () => {
      const ctx = this.engine?.ctx;
      if (ctx === undefined) return;
      void ctx.resume().then(() => {
        if (ctx.state !== 'running') return;
        this.ready = true;
        this.score?.start();
        this.score?.setState(this.wantedState);
        window.removeEventListener('pointerdown', unlock);
        window.removeEventListener('keydown', unlock);
        window.removeEventListener('touchend', unlock);
        this.gestureArmed = false;
      });
    };
    window.addEventListener('pointerdown', unlock);
    window.addEventListener('keydown', unlock);
    window.addEventListener('touchend', unlock);
  }

  private bindLifecycle(): void {
    if (typeof document === 'undefined') return;
    document.addEventListener('visibilitychange', this.onVisibility);
  }

  private readonly onVisibility = (): void => {
    if (document.hidden) this.suspend();
    else this.resume();
  };

  private applyVolumes(): void {
    const e = this.engine;
    if (e === null) return;
    const m = this.settings.muted ? 0 : 1;
    e.setMaster(clamp(this.settings.master, 0, 1) * m);
    e.setMusic(clamp(this.settings.music, 0, 1));
    e.setSfx(clamp(this.settings.sfx, 0, 1));
  }

  // ─────────────────────────────────────────────────────────────── surface ──

  play(name: SfxName, opts?: SfxOptions): SfxHandle | null {
    const e = this.engine;
    if (e === null || !this.ready) return null;
    if (opts !== undefined && opts.pan === undefined && opts.x !== undefined) {
      // Derive pan from buffer x once, here, so callers never do it twice.
      panned.x = opts.x;
      panned.pan = panForX(opts.x);
      panned.gain = opts.gain;
      panned.detune = opts.detune;
      panned.rate = opts.rate;
      panned.delay = opts.delay;
      panned.tier = opts.tier;
      panned.loop = opts.loop;
      return playSfx(e, name, panned);
    }
    return playSfx(e, name, opts);
  }

  /** Convenience wrappers so gameplay code never builds a name by hand. */
  playShoot(family: string, x: number, gain = 1): SfxHandle | null {
    return this.play(shootSfx(family), withXY(x, gain));
  }

  playDeath(family: string, x: number, gain = 1): SfxHandle | null {
    return this.play(deathSfx(family), withXY(x, gain));
  }

  setBus(name: BusName, gain: number): void {
    const v = clamp(gain, 0, 1);
    if (name === 'master') this.settings.master = v;
    else if (name === 'music') this.settings.music = v;
    else this.settings.sfx = v;
    this.applyVolumes();
    saveSettings(this.settings);
  }

  getBus(name: BusName): number {
    return name === 'master' ? this.settings.master : name === 'music' ? this.settings.music : this.settings.sfx;
  }

  setMuted(v: boolean): void {
    this.settings.muted = v;
    this.applyVolumes();
    saveSettings(this.settings);
  }

  get muted(): boolean { return this.settings.muted; }

  duck(ms: number, amount?: number): void {
    this.engine?.duck(ms, amount);
  }

  setMusicState(state: MusicState): void {
    this.wantedState = state;
    this.score?.setState(state);
  }

  setMusicIntensity(v: number): void {
    this.wantedIntensity = v;
    this.score?.setIntensity(v);
  }

  setEnemyPressure(v: number): void {
    this.wantedPressure = v;
    this.score?.setPressure(v);
  }

  get musicState(): MusicState { return this.score?.currentState ?? this.wantedState; }
  get bpm(): number { return this.score?.currentBpm ?? 0; }

  suspend(): void {
    const ctx = this.engine?.ctx;
    if (ctx === undefined || ctx.state !== 'running') return;
    void ctx.suspend();
  }

  resume(): void {
    const ctx = this.engine?.ctx;
    if (ctx === undefined || ctx.state !== 'suspended') return;
    void ctx.resume();
  }

  panic(): void { this.engine?.panic(); }

  dispose(): void {
    this.score?.dispose();
    if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', this.onVisibility);
    const ctx = this.engine?.ctx;
    this.engine = null;
    this.score = null;
    this.ready = false;
    this.initPromise = null;
    if (ctx !== undefined) void ctx.close().catch(() => { /* already closed */ });
  }
}

/** Reused option carriers. `play` is on the 30-shots-per-second path. */
const panned: SfxOptions = {};
const xy: SfxOptions = {};
function withXY(x: number, gain: number): SfxOptions {
  xy.x = undefined;
  xy.pan = panForX(x);
  xy.gain = gain;
  xy.detune = undefined;
  xy.rate = undefined;
  xy.delay = undefined;
  xy.tier = undefined;
  xy.loop = undefined;
  return xy;
}

/** The one instance. `main.ts` calls `init()` from the first click. */
export const audio = new GameAudio();
